import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { Subject, BehaviorSubject, takeUntil, Subscription } from 'rxjs';
import { SocketService } from '../../../core/service/socket/socket.service';
import { SocketNoticeService } from '../../../core/service/socket/socket-notice.service';
import { LocalStorageService } from '../../../core/service/localStorage/local-storage.service';
import { NoticeBoard } from '../../../core/service/notice/notice.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { PopupServiceService } from '../../../core/service/popup/popup-service.service';
import { SharedModule } from '../../../shared/shared.module';
import { LoaderService } from '../../../core/service/loader.service';

type NotificationRaw = {
  id: string;
  title?: string;
  referenceId?: string;
  isRead?: string | boolean; // server returns 'true'/'false' strings
  commentData?: { date?: number; type?: string } | null;
  [k: string]: any;
};

type NotificationView = {
  id: string;
  titleText: string;
  time: string;
  referenceId?: string;
  isRead: boolean;
  type?: string;
  raw: NotificationRaw;
};
interface PaginationType {
  size?: number,
  total?: number,
  skip: number,
  limit: number
}

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatMenuModule, MatBadgeModule, SharedModule],
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  providers: [SocketService, SocketNoticeService],
})
export class NotificationsComponent implements OnInit {

  private _allNotifications$ = new BehaviorSubject<NotificationRaw[]>([]);
  readonly notifications$ = this._allNotifications$; // If you want derived, map to view

  // Derived observables / subjects
  readonly topNotifications$ = new BehaviorSubject<NotificationView[]>([]);
  readonly unreadCount$ = new BehaviorSubject<number>(0);

  pagination_config: PaginationType = {
    size: 10,
    total: 0,
    skip: 0,
    limit: 10,
  };
  currentUserData: any = null;
  notifications: any[] = [];
  // notifications: any
  allNotifications: any[] = [];
  count: number = 0;
  private subscription!: Subscription;
  private _unsubscribeAll: Subject<any> = new Subject();

  // ============================================================
  // 🔹 Dependencies & Services
  // ============================================================
  private localStorageService = inject(LocalStorageService);

  constructor(
    private _notificationService: NoticeBoard,
    private _toasterService: PopupServiceService,
    private _socketService: SocketService,
    //  private translate: TranslateService,
    private _loaderService: LoaderService,
    private router: Router,
    // private _appMasterService: ApplicationMasterService,
    // private sanitizer: DomSanitizer
  ) { }

  async ngOnInit(): Promise<void> {
    this.getInitialData();
    await this.loadCurrentUser();
    const userId = this.currentUserData?.id;

    // this._socketService
    //   .customSocketOn('connect')
    //   .subscribe((data: any) => {
    //     this._socketService.emitToChannel('application-notification', {
    //       userId: userId,
    //       userType: 'admin',
    //     });
    //   });

    // // Start the interval for subsequent calls
    // this._socketService
    //   .listenToChannel('application-notification')
    //   .subscribe((data: any) => {
    //     if (
    //       data &&
    //       data?.sourceArray
    //         .map((item: any) => item?.userId)
    //         .includes(userId) &&
    //       data?.userType === 'admin'
    //     ) {
    //       this.allNotifications.push(data);
    //       this.count = this.allNotifications.filter(
    //         (item) => item?.isRead === 'false'
    //       )?.length;
    //       this.notifications = this.formatData(this.allNotifications);

    //     }
    //   });
  }

  // ============================================================
  // 🔹 Load Current User
  // ============================================================
  async loadCurrentUser() {
    try {
      const res = await this.localStorageService.getKeyIndexedDbKey('currentUser');
      this.currentUserData = res;
    } catch (err) {
      console.error('Error loading current user:', err);
    }
  }

  getInitialData() {
    this._notificationService
      .getNotification({}, 'false')
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (res: any) => {
          if (
            res?.statusCode === 200 &&
            res?.success &&
            res?.data?.data.length > 0
          ) {
            this.allNotifications = res?.data?.data;
            this.count = this.allNotifications.filter(
              (item) => item?.isRead === 'false'
            )?.length;
            this.notifications = this.formatData(res?.data?.data);
          }
        },
      });
  }

  removeNotification(i: number) {
    const data = this.notifications.splice(i, 1);
    const index = this.allNotifications.findIndex(
      (item) => item?.id === data[0]?.id
    );
    if (index !== -1) {
      this.allNotifications.splice(index, 1);
    }
  }

  formatData(dataArr: any = []) {
    return dataArr
      .filter((item: any) => item?.isRead === 'false')
      .sort((a: any, b: any) => b?.commentData?.date - a?.commentData?.date)
      .slice(0, 5)
      .map((item: any) => ({
        id: item?.id,
        title: item?.title,
        time: this._notificationService.getTimeDifference(
          item?.commentData?.date
        ),
        referenceId: item?.referenceId,
        isRead: item?.isRead === 'true',
        type: item?.commentData ? item?.commentData?.type : '',
      }));
  }

  markAsRead(
    referenceId: string,
    status: boolean = false,
    isRedirect: boolean = false,
    type: string = '',
    refId: string = ''
  ) {
    this._notificationService
      .updateNotification(referenceId, status)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          if (!isRedirect) {
            this._toasterService.successAndErrorDialog(
              'success',
              'Notification marked as read'
            );
          }
          this.getInitialData();
          if (isRedirect) {
            this.searchApplicant(refId, type);
          }
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
        },
      });
  }

  routeTo(referenceId: string, type: string) {
    this.router.navigate(
      ['/agent/applications-history/view-application', referenceId],
      {
        queryParams: { type },
      }
    );
  }

  updateData() {
    this.notifications = this.formatData(this.allNotifications);
  }

  searchApplicant(event: string = '', type: string) {
    this._loaderService.show();
    this._notificationService
      .globalSearch(this.pagination_config, event)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        (result) => {
          if (result) {
            this._loaderService.hide();
            result = result?.data?.data[0];
            const id = result?.id;
            const masterId = result?.masterId;
            const universityId = result?.coursesEnrolementRef?.universityId;
            const url = this.router.createUrlTree(
              [
                '/dashboard',
                'all-application',
                'info',
                id,
                masterId,
                universityId
              ],
              {
                queryParams: { type },
              }
            );
            this.router.navigateByUrl(url);
          }
        },
        (err) => {
          this._loaderService.hide();
        }
      );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
